import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextInput, ViewContext } from 'components/lib';
import Grid from '@mui/material/Grid';
import { Card, Paper } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { DeleteIcon, VerticalDotsIcon, FolderIcon, RefreshIcon } from 'components/icons';
import { ProjectNode } from 'types/project';
import EditableLabel from './components/EditableLabel';
import { deleteProjectReport, getProjectReports, updateProjectReport } from '../../api/projectApi';

const DeleteIconComponent = DeleteIcon as React.ComponentType<any>;
const FolderIconComponent = FolderIcon as React.ComponentType<any>;
const VerticalDotsIconComponent = VerticalDotsIcon as React.ComponentType<any>;
const RefreshIconComponent = RefreshIcon as React.ComponentType<any>;

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

type Report = {
  id: number | string;
  name?: string;
  deletedAt?: string | Date | null;
  [key: string]: any;
};

const Reports: React.FC<{ currentProject?: ProjectNode | null; showDeletedItems?: boolean }> = ({
  currentProject,
  showDeletedItems = false,
}) => {
  const context = useContext(ViewContext);
  const navigate = useNavigate();
  const [reports, setReports] = useState<Report[]>([]);
  const [willBeUpdatedReport, setWillBeUpdatedReport] = useState<Report | Record<string, any>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const visibleReports = reports.filter((report) => showDeletedItems || !report.deletedAt);

  const getReports = async () => {
    if (!currentProject?.id) {
      setReports([]);
      return;
    }

    const projectId = String(currentProject.id);

    try {
      setIsRefreshing(true);
      const data = await getProjectReports(projectId);
      setReports(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    getReports();
  }, [currentProject]);

  // edit modal
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const handleCloseUpdateModal = () => setOpenUpdateModal(false);

  const handleInputsChange = (name: string, value: any) => {
    setWillBeUpdatedReport((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleUpdateReportName = async () => {
    await updateProjectReport(String(currentProject?.id), String(willBeUpdatedReport?.id), {
      name: `${willBeUpdatedReport.name}`,
    });
    handleCloseUpdateModal();
    await getReports();
    context.notification.show(`Report name was updated`, 'success', true, 'toast');
  };

  const updateReportNameInState = (reportId: string, name: string) => {
    setReports((prevReports) =>
      prevReports.map((report) => (String(report.id) === reportId ? { ...report, name } : report))
    );
  };

  const handleInlineUpdateReportName = async (reportId: string, name: string) => {
    await updateProjectReport(String(currentProject?.id), reportId, {
      name,
    });
    updateReportNameInState(reportId, name);
  };

  const handleDeleteReport = async (pReport: Report) => {
    context.modal.show(
      {
        title: `Delete ${pReport.name}`,
        form: {},
        buttonText: 'Delete',
        text: `Are you sure you want to delete ${pReport.name}?`,
        customSubmit: () => deleteProjectReport(String(currentProject?.id), String(pReport.id)),
        destructive: true,
      },
      () => {
        getReports();
        context.notification.show(`${pReport.name} was deleted`, 'success', true, 'toast');
      }
    );
  };

  // 3 point for actions
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);

  const handleClickThreeButton = (event: React.MouseEvent<HTMLElement>, pReport: Report) => {
    setAnchorEl(event.currentTarget);
    setSelectedReport(pReport);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedReport(null);
  };

  return (
    <>
      {visibleReports.length === 0 ? (
        <Grid size={12}></Grid>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '20px', marginTop: '40px', gap: 1.5 }}>
            <h2 style={{ margin: 0, fontWeight: 'bold' }}>Observation Reports</h2>
            <IconButton onClick={getReports}>
              <RefreshIconComponent />
            </IconButton>
          </Box>
          <Grid container spacing={2}>
            {isRefreshing && (
              <Grid size={12}>
                <Card className="stat">
                  <p>Loading reports...</p>
                </Card>
              </Grid>
            )}
            {visibleReports.map((report) => {
              const reportLink = `/projects/${currentProject?.id}/observations?report=${report?.id}`;
              return (
                // <--- return was missing here
                <Grid size={3} key={report.id}>
                  <Paper
                    sx={{ px: 2, py: 0.5, minHeight: 48, cursor: 'pointer' }}
                    className="stat observation-records-card"
                    onClick={() => {
                      if (anchorEl) {
                        return;
                      }
                      navigate(reportLink);
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                        <span style={{ cursor: 'pointer' }}>
                          <FolderIconComponent />
                        </span>
                        <Box
                          sx={{
                            cursor: 'text',
                            fontSize: '1rem',
                            fontWeight: 400,
                            lineHeight: 1.5,
                            minWidth: 0,
                            flex: 1,
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                          onDoubleClick={(event) => {
                            event.stopPropagation();
                          }}
                        >
                          <EditableLabel
                            label={report?.name ?? ''}
                            item={report}
                            onEdit={(value) => updateReportNameInState(String(report.id), value)}
                            onSave={(value) => handleInlineUpdateReportName(String(report.id), value)}
                            onSingleClick={() => navigate(reportLink)}
                            useTruncation
                            placeholder="Untitled report"
                          />
                        </Box>
                      </div>
                      <IconButton
                        size="small"
                        sx={{ right: -10 }}
                        aria-label="more"
                        aria-controls="long-menu"
                        aria-haspopup="true"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleClickThreeButton(event, report);
                        }}
                      >
                        <VerticalDotsIconComponent />
                      </IconButton>
                    </div>

                    <Menu
                      anchorEl={anchorEl}
                      keepMounted
                      open={Boolean(anchorEl)}
                      onClose={(_event, _reason) => {
                        handleClose();
                      }}
                      className="actions-menu"
                    >
                      <MenuItem
                        className="actions-menu-item"
                        onClick={() => {
                          if (selectedReport) {
                            handleDeleteReport(selectedReport);
                          }
                          handleClose();
                        }}
                      >
                        <Tooltip title={`Delete ${selectedReport?.name}`}>
                          <IconButton aria-label="delete" color="error">
                            <DeleteIconComponent />
                          </IconButton>
                        </Tooltip>
                      </MenuItem>
                    </Menu>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* Update Modal */}
      <Modal
        open={openUpdateModal}
        onClose={handleCloseUpdateModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className="custom-modal"
      >
        <Box sx={style}>
          <br />
          <TextInput label="Report Name" name="name" value={willBeUpdatedReport?.name} onChange={handleInputsChange} />
          <br />
          <Button
            variant="contained"
            style={{ marginRight: '10px' }}
            className="modal-yes-btn"
            onClick={handleUpdateReportName}
          >
            Save
          </Button>
          <Button variant="contained" className="modal-no-btn" onClick={handleCloseUpdateModal}>
            Cancel
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default Reports;
